import { useUiOverlayContext } from "@/components/common/ui-overlay-provider";
import type { AnnotationCreationData } from "@/components/ui/annotations/annotation-props";
import { CreateAnnotationForm } from "@/components/ui/annotations/create-annotation-form";
import { EventType } from "@/analytics/analytics-events";
import type { CreateAnnotationEventProperties } from "@/analytics/annotation-events-properties";
import { useCurrentScene } from "@/modes/mode-data-context";
import { setHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { useAppDispatch } from "@/store/store-hooks";
import {
  setObjectVisibility,
  ViewObjectTypes,
} from "@/store/view-options/view-options-slice";
import {
  cameraToAnnotationCameraParams,
  useCreateSinglePointAnnotation,
} from "@/tools/annotation-picker/use-create-single-point-annotation";
import { FaroDialog, resizeImage, type FileDetails } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { generateGUID, type GUID } from "@faro-lotv/foundation";
import { IElementTypeHint } from "@faro-lotv/ielement-types";
import { isIElementVideoModeCopy } from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { useCallback, type RefObject } from "react";
import type { Mesh, Vector3Tuple } from "three";
import { createBoundingBoxScreenshot } from "./bounding-box-screenshot";

type UseBoundingBoxAnnotationParams = {
  /** World-space position where the annotation should be created. */
  centerWorld: Vector3Tuple;
  /** ID of the IElement the annotation is parented to. */
  elementId: GUID;
  /** Label used to pre-fill the annotation description and as a fallback title. */
  label: string;
  /** Optional shorter title used to pre-fill the annotation title. */
  title?: string;
  /** Ref to the bounding box mesh, used to capture a screenshot attachment. */
  meshRef: RefObject<Mesh | null>;
};

type UseBoundingBoxAnnotationResult = {
  /** Opens a dialog to create an annotation from the bounding box. */
  createBboxAnnotation(): void;
};

/**
 * Encapsulates the annotation creation flow for a bounding box: opening the
 * creation form dialog, saving a single-point annotation at the box center, and
 * ensuring the resulting annotation is visible.
 *
 * @param params The annotation target position, parent element and label.
 * @returns A callback that opens the annotation creation dialog.
 */
export function useBoundingBoxAnnotation({
  centerWorld,
  elementId,
  label,
  title,
  meshRef,
}: UseBoundingBoxAnnotationParams): UseBoundingBoxAnnotationResult {
  const dispatch = useAppDispatch();
  const { setContent } = useUiOverlayContext();
  const createSinglePointAnnotation = useCreateSinglePointAnnotation();
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);

  // Whether the annotation is being created while viewing a VideoMode copy
  const { referenceElement } = useCurrentScene();
  const inVideoModeCopy =
    !!referenceElement && isIElementVideoModeCopy(referenceElement);

  const saveAnnotation = useCallback(
    async (details: AnnotationCreationData) => {
      await createSinglePointAnnotation(
        {
          points: [centerWorld],
          parentId: elementId,
          type: IElementTypeHint.spaceAnnotation,
        },
        details,
        cameraToAnnotationCameraParams(camera),
      );

      setContent(undefined);

      // Ensure the new generated annotation is visible
      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.annotations,
          visibility: true,
        }),
      );
      dispatch(setHasCreatedNewAnnotation(true));
    },
    [
      createSinglePointAnnotation,
      centerWorld,
      elementId,
      camera,
      dispatch,
      setContent,
    ],
  );

  const openAnnotationForm = useCallback(
    (initialAttachment?: FileDetails) => {
      Analytics.track<CreateAnnotationEventProperties>(
        EventType.createAnnotation,
        { shape: "point", inVideoModeCopy, via: "ai annotation" },
      );

      const words = label.split(" ");
      const shortenedLabel =
        words.length > 4 ? `${words.slice(0, 4).join(" ")}...` : label;

      setContent(
        <FaroDialog
          open
          title={null}
          // Hide the actual FaroDialog and give all the space to CreateAnnotationForm
          sx={{ "& .MuiDialog-paper": { p: 1, background: "transparent" } }}
        >
          <CreateAnnotationForm
            onClose={() => setContent(undefined)}
            onSave={saveAnnotation}
            // Prefer the AI-provided title when available, otherwise fall back to a
            // shortened version of the label.
            initialTitle={title ?? shortenedLabel}
            initialDescription={label}
            initialAttachment={initialAttachment}
          />
        </FaroDialog>,
      );
    },
    [label, title, saveAnnotation, setContent, inVideoModeCopy],
  );

  const createBboxAnnotation = useCallback(async () => {
    const mesh = meshRef.current;
    if (!mesh) {
      openAnnotationForm();
      return;
    }

    try {
      const screenshot = await createBoundingBoxScreenshot(
        mesh,
        gl,
        scene,
        camera,
      );
      const thumbnailUrl = await resizeImage(screenshot).catch(() => null);
      openAnnotationForm({
        id: generateGUID(),
        date: new Date().toISOString(),
        fileName: screenshot.name,
        fileSize: screenshot.size,
        urlOrFile: screenshot,
        thumbnailUrl,
      });
    } catch {
      // If the screenshot cannot be captured, still allow creating the
      // annotation without a pre-seeded attachment.
      openAnnotationForm();
    }
  }, [camera, gl, scene, meshRef, openAnnotationForm]);

  return { createBboxAnnotation };
}
